import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  X,
  Envelope,
  Phone,
  MapPin,
  Lightning,
  User,
  Calendar,
  CurrencyDollar,
  Receipt,
  ClockCounterClockwise,
  Warning,
  CheckCircle,
  Copy,
  IdentificationBadge,
  Percent,
  PencilSimple,
  FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
}
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    active: { text: "Activo", cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-400", dot: "bg-emerald-500" },
    disconnected: { text: "Desconectado", cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-400", dot: "bg-rose-500" },
    inactive: { text: "Inactivo", cls: "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400", dot: "bg-slate-400" },
    pending: { text: "Pendiente", cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-400", dot: "bg-amber-500" },
  };
  const cfg = map[status] || map.inactive;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cfg.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.text}
    </span>
  );
}

function InvoicePill({ status }) {
  const map = {
    paid: { text: "Pagada", cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-400" },
    open: { text: "Abierta", cls: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300" },
    sent: { text: "Enviada", cls: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-400" },
    overdue: { text: "Vencida", cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-400" },
    void: { text: "Anulada", cls: "border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-500" },
    refunded: { text: "Reembolsada", cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-400" },
  };
  const cfg = map[status] || map.open;
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cfg.cls}`}>
      {cfg.text}
    </span>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <section className="px-6 py-5 border-b border-slate-100 dark:border-slate-900">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={12} className="text-slate-400" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
          {title}
        </div>
        {count !== undefined && (
          <span className="text-[10px] text-slate-400 font-mono tabular-nums">{count}</span>
        )}
        <div className="flex-1 h-px bg-slate-100 dark:bg-slate-900" />
      </div>
      {children}
    </section>
  );
}

function KV({ label, value, mono = false, className = "" }) {
  return (
    <div className={`space-y-0.5 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
      <div className={`text-sm text-slate-900 dark:text-slate-100 ${mono ? "font-mono tabular-nums" : ""}`}>
        {value || <span className="text-slate-400 italic">—</span>}
      </div>
    </div>
  );
}

export default function ClientProfile360({ clientId, open, onOpenChange, startInEdit = false, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    setData(null);
    setEditing(false);
    api.get(`/clients/${clientId}/profile-360`)
      .then(({ data }) => {
        setData(data);
        setForm({
          name: data.client.name || "",
          email: data.client.email || "",
          phone: data.client.phone || "",
          address: data.client.address || "",
          city: data.client.city || "",
          state: data.client.state || "",
          zip_code: data.client.zip_code || "",
          monthly_fee: data.client.monthly_fee ?? "",
          status: data.client.status || "active",
          notes: data.client.notes || "",
          birth_date: data.client.birth_date || data.client.dob || "",
          preferred_language: data.client.preferred_language || "",
          esi_id: data.client.esi_id || "",
          payment_method: data.client.payment_method || "",
        });
        if (startInEdit) setEditing(true);
      })
      .catch((e) => toast.error(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [clientId, open, startInEdit]);

  // Close on ESC (but do NOT dim the background — keep table visible & interactive)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !editing) onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange, editing]);

  const copy = (text, label = "Copiado") => {
    navigator.clipboard.writeText(text || "");
    toast.success(label);
  };

  const startEdit = () => {
    if (data) setEditing(true);
  };

  const cancelEdit = () => {
    if (!data) return setEditing(false);
    setForm({
      name: data.client.name || "",
      email: data.client.email || "",
      phone: data.client.phone || "",
      address: data.client.address || "",
      city: data.client.city || "",
      state: data.client.state || "",
      zip_code: data.client.zip_code || "",
      monthly_fee: data.client.monthly_fee ?? "",
      status: data.client.status || "active",
      notes: data.client.notes || "",
      birth_date: data.client.birth_date || data.client.dob || "",
      preferred_language: data.client.preferred_language || "",
      esi_id: data.client.esi_id || "",
      payment_method: data.client.payment_method || "",
    });
    setEditing(false);
  };

  const save = async () => {
    if (!form?.name?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.monthly_fee === "") delete payload.monthly_fee;
      else payload.monthly_fee = Number(payload.monthly_fee);
      if (!payload.birth_date) delete payload.birth_date;
      const { data: updated } = await api.put(`/clients/${clientId}`, payload);
      toast.success("Cambios guardados");
      // Refetch fresh profile-360 to get consistent aggregations
      const { data: fresh } = await api.get(`/clients/${clientId}/profile-360`);
      setData(fresh);
      setEditing(false);
      if (onSaved) onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed right-0 top-0 bottom-0 z-40 w-full sm:max-w-2xl bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 shadow-[-8px_0_24px_-8px_rgba(0,0,0,0.15)] overflow-y-auto animate-in slide-in-from-right-full duration-300"
      data-testid="client-profile-360"
    >
        {loading || !data ? (
          <div className="flex items-center justify-center h-full py-20 text-sm text-slate-500">
            Cargando perfil…
          </div>
        ) : (
          <>
            {/* ── Header ── */}
            <header className="sticky top-0 z-10 px-6 py-4 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                    Perfil 360°
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {data.client.name}
                    </h2>
                    <StatusPill status={data.client.status} />
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    {data.client.referral_code && (
                      <button onClick={() => copy(data.client.referral_code, "Código copiado")} className="hover:text-slate-700 dark:hover:text-slate-300 inline-flex items-center gap-0.5" title="Copiar código de referido">
                        <IdentificationBadge size={10} /> {data.client.referral_code} <Copy size={9} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!editing ? (
                    <button
                      onClick={startEdit}
                      data-testid="profile-360-edit"
                      className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors"
                      title="Editar cliente"
                    >
                      <PencilSimple size={12} /> Editar
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        data-testid="profile-360-cancel"
                        className="h-8 px-2.5 text-[11px] font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={save}
                        disabled={saving || !form?.name?.trim()}
                        data-testid="profile-360-save"
                        className="inline-flex items-center gap-1 h-8 px-3 text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white rounded transition-colors disabled:opacity-50"
                      >
                        <FloppyDisk size={12} /> {saving ? "Guardando…" : "Guardar"}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => onOpenChange(false)}
                    data-testid="close-profile-360"
                    className="p-1.5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </header>

            {/* ── Financial summary strip ── */}
            <div className="grid grid-cols-4 divide-x divide-slate-200 dark:divide-slate-800 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
              <div className="px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Tarifa</div>
                <div className="mt-0.5 font-mono tabular-nums text-base font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(data.client.monthly_fee)}</div>
                <div className="text-[10px] text-slate-500">/mes</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Pagado (LTV)</div>
                <div className="mt-0.5 font-mono tabular-nums text-base font-semibold text-emerald-700 dark:text-emerald-400">{fmtMoney(data.financial.lifetime_value)}</div>
                <div className="text-[10px] text-slate-500">{data.financial.invoices_paid} facturas</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Abierto</div>
                <div className="mt-0.5 font-mono tabular-nums text-base font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(data.financial.total_open)}</div>
                <div className="text-[10px] text-slate-500">
                  {data.financial.invoices_total - data.financial.invoices_paid} pendientes
                </div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Vencido</div>
                <div className={`mt-0.5 font-mono tabular-nums text-base font-semibold ${data.financial.total_overdue > 0 ? "text-rose-700 dark:text-rose-400" : "text-slate-900 dark:text-slate-100"}`}>
                  {fmtMoney(data.financial.total_overdue)}
                </div>
                <div className="text-[10px] text-slate-500">{data.financial.invoices_overdue} facturas</div>
              </div>
            </div>

            {/* ── Contact ── */}
            <Section icon={User} title="Contacto">
              {!editing ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <KV label="Nombre" className="col-span-2" value={data.client.name} />
                  <KV label="Email" value={data.client.email ? (
                    <a href={`mailto:${data.client.email}`} className="inline-flex items-center gap-1 hover:underline">
                      <Envelope size={12} className="text-slate-400" />
                      {data.client.email}
                    </a>
                  ) : null} />
                  <KV label="Teléfono" value={data.client.phone ? (
                    <a href={`tel:${data.client.phone}`} className="inline-flex items-center gap-1 hover:underline font-mono">
                      <Phone size={12} className="text-slate-400" />
                      {data.client.phone}
                    </a>
                  ) : null} />
                  <KV label="Fecha de nacimiento" value={formatDate(data.client.birth_date || data.client.dob)} />
                  <KV label="Idioma preferido" value={data.client.preferred_language || null} />
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                  <EditField label="Nombre *" span={12} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
                  <EditField label="Email" span={7} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="edit-email" />
                  <EditField label="Teléfono" span={5} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />
                  <EditField label="Fecha de nacimiento" span={6} type="date" value={form.birth_date?.slice(0,10) || ""} onChange={(v) => setForm({ ...form, birth_date: v })} testid="edit-dob" />
                  <EditField label="Idioma" span={6} value={form.preferred_language} onChange={(v) => setForm({ ...form, preferred_language: v })} testid="edit-lang" placeholder="ES / EN" />
                </div>
              )}
            </Section>

            {/* ── Service address ── */}
            <Section icon={MapPin} title="Dirección de servicio">
              {!editing ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <KV
                    label="Dirección"
                    className="col-span-2"
                    value={
                      <div>
                        <div>{data.client.address || "—"}</div>
                        {(data.client.city || data.client.zip_code) && (
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {[data.client.city, data.client.state, data.client.zip_code].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    }
                  />
                  <KV label="ESI ID" value={data.client.esi_id} mono />
                  <KV label="Método de pago" value={data.client.payment_method} />
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                  <EditField label="Dirección" span={12} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="edit-address" />
                  <EditField label="Ciudad" span={5} value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="edit-city" />
                  <EditField label="Estado" span={3} value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="edit-state" placeholder="TX" />
                  <EditField label="ZIP" span={4} value={form.zip_code} onChange={(v) => setForm({ ...form, zip_code: v })} testid="edit-zip" mono />
                  <EditField label="ESI ID" span={7} value={form.esi_id} onChange={(v) => setForm({ ...form, esi_id: v })} testid="edit-esi" mono />
                  <EditField label="Método de pago" span={5} value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} testid="edit-payment" />
                </div>
              )}
              {!editing && data.address_history && data.address_history.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-900">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold mb-2">
                    Historial de traslados ({data.address_history.length})
                  </div>
                  <ul className="space-y-1.5">
                    {data.address_history.map((h, i) => (
                      <li key={h.id || h.changed_at || `addr-${i}`} className="flex items-start gap-2 text-[12px]">
                        <ClockCounterClockwise size={11} className="text-slate-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-slate-700 dark:text-slate-300">{h.old_address || h.address}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{formatDateTime(h.changed_at)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* ── Plan & Agent ── */}
            <Section icon={Lightning} title="Plan y servicio">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KV label="Plan actual" value={data.plan?.name} />
                <KV label="Proveedor (REP)" value={data.plan?.rep_name} />
                <KV label="Tarifa kWh" value={data.plan?.kwh_rate ? `${Number(data.plan.kwh_rate).toFixed(4)}¢` : null} mono />
                <KV label="Término" value={data.plan?.term_months ? `${data.plan.term_months} meses` : null} />
                <KV label="Inicio del servicio" value={formatDate(data.client.service_start_date)} />
                <KV label="Día de facturación" value={data.client.billing_day ? `Día ${data.client.billing_day}` : null} />
              </div>
              {data.agent && (
                <div className="mt-4 px-3 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <IdentificationBadge size={14} className="text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Referido por</div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{data.agent.name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1 justify-end">
                      <Percent size={9} /> Comisión
                    </div>
                    <div className="font-mono tabular-nums text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {(data.agent.commission_pct || 0).toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}
              {!data.agent && data.client.referrer_name && (
                <div className="mt-3 text-[11px] text-slate-500 italic">
                  Referrer sin resolver: <strong className="text-slate-700 dark:text-slate-300">{data.client.referrer_name}</strong>
                  <span className="ml-2">→ ve a "Agentes / Referidos" para vincularlo</span>
                </div>
              )}
            </Section>

            {/* ── Commercial (editable) ── */}
            <Section icon={CurrencyDollar} title="Detalles comerciales">
              {!editing ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <KV label="Tarifa mensual" value={fmtMoney(data.client.monthly_fee)} mono />
                  <KV label="Estado" value={<StatusPill status={data.client.status} />} />
                  <KV
                    label="Notas internas"
                    className="col-span-2"
                    value={data.client.notes ? (
                      <div className="text-[13px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                        {data.client.notes}
                      </div>
                    ) : null}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                  <EditField
                    label="Tarifa mensual (USD)"
                    span={6}
                    type="number"
                    step="0.01"
                    value={form.monthly_fee}
                    onChange={(v) => setForm({ ...form, monthly_fee: v })}
                    testid="edit-fee"
                    mono
                  />
                  <div className="col-span-12 sm:col-span-6 space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Estado</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="edit-status"
                      className="w-full h-9 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                      <option value="pending">Pendiente</option>
                      <option value="disconnected">Desconectado</option>
                    </select>
                  </div>
                  <div className="col-span-12 space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Notas internas</label>
                    <Textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="edit-notes"
                      className="text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
                    />
                  </div>
                </div>
              )}
            </Section>

            {/* ── Credentials (masked) ── */}
            {(data.client.utility_username || data.client.utility_provider) && (
              <Section icon={IdentificationBadge} title="Credenciales de servicio">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <KV label="Proveedor" value={data.client.utility_provider} />
                  <KV label="Usuario" value={data.client.utility_username} mono />
                  <KV label="Contraseña" value={data.client.utility_password ? "••••••••" : null} mono />
                  <KV label="Cuenta" value={data.client.utility_account_number} mono />
                </div>
              </Section>
            )}

            {/* ── Invoices ── */}
            <Section icon={Receipt} title="Facturas" count={data.invoices.length}>
              {data.invoices.length === 0 ? (
                <div className="text-[12px] text-slate-500 italic">Sin facturas registradas</div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-900 -mx-2">
                  {data.invoices.slice(0, 12).map((inv) => (
                    <li key={inv.id} className="px-2 py-2 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-slate-900/40 rounded">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <InvoicePill status={inv.status} />
                          <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400 truncate">
                            {inv.invoice_number || inv.id?.slice(0, 8)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Emitida {formatDate(inv.created_at)}
                          {inv.due_date && <> · Vence {formatDate(inv.due_date)}</>}
                          {inv.paid_at && <> · Pagada {formatDate(inv.paid_at)}</>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono tabular-nums text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {fmtMoney(inv.amount)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {data.invoices.length > 12 && (
                <div className="mt-2 text-[11px] text-slate-500 italic">Mostrando 12 más recientes de {data.invoices.length}</div>
              )}
            </Section>

            {/* ── Activity feed ── */}
            <Section icon={ClockCounterClockwise} title="Actividad reciente" count={data.activity.length}>
              {data.activity.length === 0 ? (
                <div className="text-[12px] text-slate-500 italic">Sin actividad registrada</div>
              ) : (
                <ul className="space-y-2">
                  {data.activity.slice(0, 15).map((a, i) => (
                    <li key={a.id || i} className="flex items-start gap-2.5 text-[12px]">
                      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-700 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-700 dark:text-slate-300">
                          {a.title || a.type || "Actividad"}
                        </div>
                        {a.description && (
                          <div className="text-[11px] text-slate-500 mt-0.5">{a.description}</div>
                        )}
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">{formatDateTime(a.created_at)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* ── Outages / status ── */}
            {(data.client.has_outage || data.client.outage_note) && (
              <Section icon={Warning} title="Interrupciones activas">
                <div className="border border-orange-200 bg-orange-50 dark:border-orange-900/60 dark:bg-orange-950/30 rounded px-3 py-2 flex items-start gap-2">
                  <Warning size={14} className="text-orange-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[13px] font-semibold text-orange-800 dark:text-orange-300">Servicio con interrupción</div>
                    {data.client.outage_note && (
                      <div className="text-[11px] text-orange-700 dark:text-orange-400 mt-0.5">{data.client.outage_note}</div>
                    )}
                  </div>
                </div>
              </Section>
            )}
          </>
        )}
    </div>
  );
}

function EditField({ label, span = 12, value, onChange, testid, type = "text", placeholder = "", step, mono = false }) {
  const spanCls = {
    3: "col-span-12 sm:col-span-3",
    4: "col-span-12 sm:col-span-4",
    5: "col-span-12 sm:col-span-5",
    6: "col-span-12 sm:col-span-6",
    7: "col-span-12 sm:col-span-7",
    8: "col-span-12 sm:col-span-8",
    12: "col-span-12",
  }[span];
  return (
    <div className={`${spanCls} space-y-1`}>
      <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </label>
      <Input
        type={type}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 ${mono ? "font-mono tabular-nums" : ""}`}
      />
    </div>
  );
}
